import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FloatingBubbles from '../components/FloatingBubbles';
import Avatar from '../components/Avatar';
import axios from 'axios';

const NOTIF_ICONS = {
  like: '❤️',
  comment: '💬',
  follow: '👥',
  project_join: '🚀',
};

const NOTIF_LABELS = {
  like: 'a aimé ton post',
  comment: 'a commenté ton post',
  follow: 'te suit maintenant',
  project_join: 'a rejoint ton projet',
};

function timeAgo(date) {
  const diff = Math.floor((new Date() - new Date(date)) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)}j`;
  return new Date(date).toLocaleDateString('fr-FR');
}

export default function Notifications() {
  const { user, token, API } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers });
      setNotifications(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [API, token]);

  useEffect(() => {
    if (token) fetchNotifications();
  }, [fetchNotifications, token]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read/all`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) { console.error(err); }
  };

  const deleteAll = async () => {
    if (!window.confirm('Supprimer toutes les notifications ?')) return;
    try {
      await axios.delete(`${API}/notifications`, { headers });
      setNotifications([]);
    } catch (err) { console.error(err); }
  };

  const handleClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    // Navigate
    if (notif.type === 'follow') {
      navigate(`/profile/${notif.actor.username}`);
    } else if (notif.post_id) {
      navigate('/');
    } else if (notif.project_id) {
      navigate(`/projects/${notif.project_id}`);
    }
  };

  if (!user) return (
    <div style={{ textAlign: 'center', padding: '80px', color: 'var(--muted)' }}>
      <Link to="/login" style={{ color: 'var(--blue)', fontWeight: 600 }}>Connecte toi</Link> pour voir tes notifications.
    </div>
  );

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <>
      <FloatingBubbles />
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
        <div style={{ maxWidth: '640px', margin: '0 auto', padding: '32px 20px 48px' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--dark)', margin: 0 }}>Notifications</h1>
              {unreadCount > 0 && (
                <span style={{ background: '#E53E3E', color: '#fff', fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px' }}>
                  {unreadCount} nouvelles
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {unreadCount > 0 && (
                <button onClick={markAllRead} style={{ background: 'var(--blue-bg)', color: 'var(--blue)', border: 'none', padding: '7px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                  ✓ Tout lire
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={deleteAll} style={{ background: '#FFF5F5', color: 'var(--red)', border: 'none', padding: '7px 14px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer' }}>
                  🗑️ Tout supprimer
                </button>
              )}
            </div>
          </div>

          {/* Notifications */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--muted)' }}>Chargement...</div>
          ) : notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 20px', background: 'rgba(255,255,255,0.8)', borderRadius: '16px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
              <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dark)', marginBottom: '6px' }}>Aucune notification</p>
              <p style={{ fontSize: '14px', color: 'var(--muted)' }}>Les nouvelles activités apparaîtront ici.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {notifications.map(notif => (
                <div key={notif.id} onClick={() => handleClick(notif)}
                  style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: notif.is_read ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.95)', borderRadius: '14px', border: `1px solid ${notif.is_read ? 'var(--border)' : 'var(--blue-border)'}`, cursor: 'pointer', transition: 'all .2s', boxShadow: notif.is_read ? 'none' : 'var(--shadow)' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = notif.is_read ? 'var(--border)' : 'var(--blue-border)'}
                >
                  {/* Point non lu */}
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: notif.is_read ? 'transparent' : '#E53E3E', flexShrink: 0 }} />

                  {/* Avatar */}
                  {notif.actor && <Avatar user={notif.actor} size={40} fontSize={14} />}

                  {/* Contenu */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '14px', color: 'var(--dark)', margin: 0, lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 700 }}>{notif.actor?.username}</span>
                      {' '}{NOTIF_LABELS[notif.type] || notif.type}
                      {notif.post_content && (
                        <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}> — "{notif.post_content}"</span>
                      )}
                      {notif.project_title && (
                        <span style={{ color: 'var(--blue)', fontWeight: 600 }}> {notif.project_title}</span>
                      )}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '3px 0 0' }}>{timeAgo(notif.created_at)}</p>
                  </div>

                  {/* Icône */}
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>{NOTIF_ICONS[notif.type] || '🔔'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
